import AnalysisType, { AnalysisTypeStrings } from 'enums/AnalysisType'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import messageUtil from 'utils/messageUtil'
import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import TopicComparisonMeasureType, {
  TopicComparisonMeasureTypeStrings
} from 'enums/TopicComparisonMeasureType'
import i18n from '../i18n'

export const SW_SUMMARY_MEASURE_TYPE = [
  MeasureType.MENTIONS,
  MeasureType.POSTS,
  MeasureType.NET_SENTIMENT,
  MeasureType.PASSION_INTENSITY,
  MeasureType.POSITIVES,
  MeasureType.NEGATIVES,
  MeasureType.IMPRESSIONS,
  MeasureType.UNIQUE_AUTHORS
]

export const SW_NEW_SUMMARY_MEASURE_TYPE = [
  MeasureType.MENTIONS,
  MeasureType.POSTS,
  MeasureType.NET_SENTIMENT,
  MeasureType.PASSION_INTENSITY,
  MeasureType.POSITIVES,
  MeasureType.NEGATIVES,
  MeasureType.ENGAGEMENTS,
  MeasureType.COMMENTS,
  MeasureType.LIKES,
  MeasureType.SHARES,
  MeasureType.SCORE,
  MeasureType.ENGAGEMENT_RATIO,
  MeasureType.IMPRESSIONS,
  MeasureType.UNIQUE_AUTHORS,
  MeasureType.OVERALL_STAR_RATINGS,
  MeasureType.OVERALL_SURVEY_SCORES,
  MeasureType.NET_PROMOTER_SCORES
]

export const AM_SUMMARY_MEASURE_TYPE = [
  MeasureType.UNIQUE_AUTHORS,
  MeasureType.MENTIONS,
  MeasureType.POSTS,
  MeasureType.IMPRESSIONS,
  MeasureType.NET_SENTIMENT,
  MeasureType.PASSION_INTENSITY,
  MeasureType.POSITIVES,
  MeasureType.NEGATIVES
]

export const MEASUREMENT_MEASURES = [MeasureType.STAR_RATINGS, MeasureType.SURVEY_SCORES]

export const SW_DEFAULT_TIMELINE_MEASURES = [MeasureType.MENTIONS]

export const AM_DEFAULT_TIMELINE_MEASURES = [MeasureType.UNIQUE_AUTHORS]

export const SW_DEFAULT_SUMMARY_MEASURE_TYPE = [
  MeasureType.MENTIONS,
  MeasureType.IMPRESSIONS,
  MeasureType.NET_SENTIMENT,
  MeasureType.NEGATIVES,
  MeasureType.POSITIVES
]

export const SW_DEFAULT_NEW_SUMMARY_MEASURE_TYPE = [
  MeasureType.MENTIONS,
  MeasureType.POSTS,
  MeasureType.IMPRESSIONS,
  MeasureType.NET_SENTIMENT,
  MeasureType.PASSION_INTENSITY
]

export const AM_DEFAULT_SUMMARY_MEASURE_TYPE = [MeasureType.UNIQUE_AUTHORS, MeasureType.MENTIONS]

/**
 * @param {string} measure
 * @return {string}
 */
export const getLabel = (
  measure: MeasureTypeStrings | MetricCategoryItemStrings | MetricSeriesStrings,
  analysisType?: AnalysisTypeStrings
) => {
  if (analysisType === AnalysisType.YT_SUMMARY) {
    switch (measure) {
      case MetricSeries.TOTAL_BUZZ_POST:
      case MeasureType.POSTS:
        return i18n.t('youtubePost_other')
      case MeasureType.ENGAGEMENTS:
        return i18n.t('statistic_other')
      default:
        break
    }
  }

  if (
    analysisType === AnalysisType.SW_NEW_SUMMARY ||
    analysisType === AnalysisType.AM_NEW_SUMMARY
  ) {
    switch (measure) {
      case MetricCategoryItem.MALE:
      case MetricCategoryItem.FEMALE:
      case MetricCategoryItem.UNKNOWN:
        return i18n.t('authorGenders', { gender: messageUtil.genders(measure) })
      default:
        break
    }
  }
  const label = messageUtil.measureType(measure)
  return label || measure
}

export const getMetricSeries = (measure: MeasureTypeStrings) => {
  switch (measure) {
    case MeasureType.MENTIONS:
      return [MetricSeries.TOTAL_BUZZ]

    case MeasureType.POSTS:
      return [MetricSeries.TOTAL_BUZZ_POST]

    case MeasureType.POST_VIDEOS:
      return [MetricSeries.POST_VIDEOS]

    case MeasureType.POST_IMAGES:
      return [MetricSeries.POST_IMAGES]

    case MeasureType.POST_OTHER:
      return [MetricSeries.POST_OTHER]

    case MeasureType.IMPRESSIONS:
      return [MetricSeries.IMPRESSIONS]

    case MeasureType.NET_SENTIMENT:
      return [MetricSeries.NET_SENTIMENT]

    case MeasureType.PASSION_INTENSITY:
      return [MetricSeries.PASSION]

    case MeasureType.POSITIVES:
      return [MetricSeries.POSITIVE_SENTIMENT]

    case MeasureType.NEGATIVES:
      return [MetricSeries.NEGATIVE_SENTIMENT]

    case MeasureType.UNIQUE_AUTHORS:
      return [MetricSeries.UNIQUE_AUTHORS]

    case MeasureType.ENGAGEMENTS:
      return [MetricSeries.ENGAGEMENTS]

    case MeasureType.COMMENTS:
      return [MetricSeries.COMMENTS]

    case MeasureType.LIKES:
      return [MetricSeries.LIKES]

    case MeasureType.SHARES:
      return [MetricSeries.SHARES]

    case MeasureType.DISLIKES:
      return [MetricSeries.DISLIKES]

    case MeasureType.VIEWS:
      return [MetricSeries.VIEWS]

    case MeasureType.SCORE:
      return [MetricSeries.SCORE]

    case MeasureType.ENGAGEMENT_RATIO:
      return [MetricSeries.ENGAGEMENT_RATIO]

    case MeasureType.ENGAGEMENT_RATIO_ONLY:
      return [MetricSeries.ENGAGEMENT_RATIO_ONLY]

    case MeasureType.OVERALL_STAR_RATINGS:
    case MeasureType.STAR_RATINGS:
      return [MetricSeries.OVERALL_STAR_RATINGS]

    case MeasureType.OVERALL_SURVEY_SCORES:
    case MeasureType.SURVEY_SCORES:
      return [MetricSeries.OVERALL_SURVEY_SCORES]

    case MeasureType.NET_PROMOTER_SCORES:
      return [MetricSeries.NET_PROMOTER_SCORES]

    case MeasureType.OVERALL_STAR_RATINGS_AVERAGE:
      return [MetricSeries.OVERALL_STAR_RATINGS_AVERAGE]

    case MeasureType.OVERALL_SURVEY_SCORES_AVERAGE:
      return [MetricSeries.OVERALL_SURVEY_SCORES_AVERAGE]

    default:
      return []
  }
}

export const formatByMeasureType = (
  value: number,
  measureType: MeasureTypeStrings,
  { compactFormatThreshold = undefined }: { compactFormatThreshold?: number }
) => {
  const percentFormatter = new Intl.NumberFormat(undefined, { style: 'percent' })
  const numberFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })
  const compactFormatter = Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
    // @ts-ignore
    // Don't know why Safari(15.6.1+) doesn't honor maximumFractionDigits.
    // Therefore, the experimental roundingPriority is added to force Safari to use the result calculated from maximumFractionDigits.
    roundingPriority: 'lessPrecision'
  })
  const shouldUseCompactFormatter =
    typeof compactFormatThreshold === 'number' && value > compactFormatThreshold

  switch (measureType) {
    case MeasureType.NET_SENTIMENT:
      return percentFormatter.format(value / 100)
    // CB-22541, display 1 decimal point.
    case MeasureType.ENGAGEMENT_RATIO:
    case MeasureType.OVERALL_STAR_RATINGS:
    case MeasureType.OVERALL_SURVEY_SCORES:
    case MeasureType.NET_PROMOTER_SCORES:
      return value.toFixed(1)
    default:
      return shouldUseCompactFormatter
        ? compactFormatter.format(value)
        : numberFormatter.format(value)
  }
}

export const formatDeltaByMeasureType = (
  measure: MeasureTypeStrings,
  val: number,
  deltaVal: number
) => {
  const percentFormatter = new Intl.NumberFormat(undefined, { style: 'percent' })
  const numberFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
  const absoluteDeltaPercentage = Math.abs((val - deltaVal) / deltaVal)

  switch (measure) {
    case MeasureType.PASSION_INTENSITY:
    case MeasureType.NET_SENTIMENT:
      return numberFormatter.format(Math.abs(val - deltaVal))
    default:
      if (absoluteDeltaPercentage > 9999.99) {
        return '>999,999%'
      }
      if (absoluteDeltaPercentage < 0.01 && absoluteDeltaPercentage > 0) {
        return '<1%'
      }
      return percentFormatter.format(absoluteDeltaPercentage)
  }
}

export const formatByTopicComparisonMeasureType = (
  value: number,
  topicComparisonMeasureType: TopicComparisonMeasureTypeStrings
) => {
  const percentFormatter = new Intl.NumberFormat(undefined, { style: 'percent' })
  const numberFormatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

  switch (topicComparisonMeasureType) {
    case TopicComparisonMeasureType.NET_SENTIMENT:
      return percentFormatter.format(value / 100)
    default:
      return numberFormatter.format(value)
  }
}

export const checkValidDelta = (measure: string, deltaVal: number) => {
  let isValidDelta = true
  if (
    [
      MeasureType.PASSION_INTENSITY,
      MeasureType.NET_SENTIMENT,
      MeasureType.NET_PROMOTER_SCORES
    ].includes(measure)
  )
    isValidDelta = deltaVal !== 0
  else isValidDelta = deltaVal > 0

  return isValidDelta
}
